/**
 * Printable "Farm Data" report — rendered off-screen and captured to PDF.
 * Deliberately plain/high-contrast (vs. the live dashboard UI) since it's meant to be printed/shared.
 * @module components/initiatives/juthoor-farm-data-report
 */

import { forwardRef } from 'react';
import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';
import type { JuthoorInitiative, JuthoorParticipantEntry } from '@/types/juthoor';

export interface JuthoorFarmDataReportProps {
  initiative: JuthoorInitiative;
  locale: Locale;
  landName: string | null;
  treesReserved: number;
  treesPlanted: number;
  treesRewarded: number;
  co2Kg: number;
  o2Kg: number;
  participants: JuthoorParticipantEntry[];
  generatedAt: string;
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 16px' }}>
      <p style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>
        {label}
      </p>
      <p style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: '4px 0 0' }}>{value}</p>
    </div>
  );
}

export const JuthoorFarmDataReport = forwardRef<HTMLDivElement, JuthoorFarmDataReportProps>(
  function JuthoorFarmDataReport(
    {
      initiative,
      locale,
      landName,
      treesReserved,
      treesPlanted,
      treesRewarded,
      co2Kg,
      o2Kg,
      participants,
      generatedAt,
    },
    ref
  ) {
    const isRTL = locale === 'ar';
    const title = isRTL ? initiative.title_ar || initiative.title : initiative.title_en || initiative.title;

    return (
      <div
        ref={ref}
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{
          width: 760,
          padding: 40,
          backgroundColor: '#ffffff',
          fontFamily: isRTL ? 'Tajawal, sans-serif' : 'Poppins, sans-serif',
          color: '#111827',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            borderBottom: '2px solid #111827',
            paddingBottom: 16,
            marginBottom: 24,
          }}
        >
          <div>
            <p style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>
              {isRTL ? 'تقرير بيانات المزرعة' : 'Farm Data Report'}
            </p>
            <h1 style={{ fontSize: 26, fontWeight: 800, margin: '4px 0 0' }}>{title}</h1>
          </div>
          <div style={{ textAlign: isRTL ? 'left' : 'right', fontSize: 11, color: '#6b7280' }}>
            <p style={{ margin: 0 }}>{isRTL ? 'أُنشئ في' : 'Generated'}</p>
            <p style={{ margin: 0, fontWeight: 600, color: '#111827' }}>{generatedAt}</p>
          </div>
        </div>

        {/* Land */}
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: '#6b7280', margin: '0 0 6px' }}>
            {t(locale, 'activeLand')}
          </p>
          <p style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
            {landName ?? `${isRTL ? 'رقم الأرض' : 'Land ID'}: ${initiative.juthoor.netzero_land_id}`}
          </p>
        </div>

        {/* Ecosystem flow */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
          <StatBlock label={t(locale, 'treesReserved')} value={treesReserved.toLocaleString()} />
          <StatBlock label={t(locale, 'treesPlanted')} value={treesPlanted.toLocaleString()} />
          <StatBlock label={t(locale, 'treesRewarded')} value={treesRewarded.toLocaleString()} />
        </div>

        {/* Sustainable impact */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 24 }}>
          <StatBlock
            label={isRTL ? 'كغ ثاني أكسيد الكربون الممتص / سنة' : 'kg CO₂ absorbed / year'}
            value={co2Kg.toLocaleString()}
          />
          <StatBlock
            label={isRTL ? 'كغ أكسجين منتج / سنة' : 'kg O₂ produced / year'}
            value={o2Kg.toLocaleString()}
          />
        </div>

        {/* Configuration summary */}
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 13, fontWeight: 700, margin: '0 0 10px' }}>
            {isRTL ? 'تفاصيل المبادرة' : 'Initiative Configuration'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, fontSize: 12 }}>
            <div>
              <p style={{ color: '#6b7280', margin: 0 }}>{t(locale, 'totalTrees')}</p>
              <p style={{ fontWeight: 600, margin: '2px 0 0' }}>{initiative.juthoor.total_trees.toLocaleString()}</p>
            </div>
            <div>
              <p style={{ color: '#6b7280', margin: 0 }}>{t(locale, 'rewardPerTree')}</p>
              <p style={{ fontWeight: 600, margin: '2px 0 0' }}>{initiative.juthoor.reward_per_tree} GP</p>
            </div>
            <div>
              <p style={{ color: '#6b7280', margin: 0 }}>{t(locale, 'maxTreesPerClient')}</p>
              <p style={{ fontWeight: 600, margin: '2px 0 0' }}>{initiative.juthoor.max_trees_per_client}</p>
            </div>
            <div>
              <p style={{ color: '#6b7280', margin: 0 }}>{t(locale, 'startDate')}</p>
              <p style={{ fontWeight: 600, margin: '2px 0 0' }}>{initiative.juthoor.start_date}</p>
            </div>
            {initiative.juthoor.budget != null && (
              <div>
                <p style={{ color: '#6b7280', margin: 0 }}>{isRTL ? 'الميزانية' : 'Budget'}</p>
                <p style={{ fontWeight: 600, margin: '2px 0 0' }}>{initiative.juthoor.budget.toLocaleString()} NC</p>
              </div>
            )}
            {initiative.juthoor.planting_date && (
              <div>
                <p style={{ color: '#6b7280', margin: 0 }}>{isRTL ? 'تاريخ الزراعة' : 'Planting Date'}</p>
                <p style={{ fontWeight: 600, margin: '2px 0 0' }}>{initiative.juthoor.planting_date}</p>
              </div>
            )}
          </div>
        </div>

        {/* Participation ledger */}
        <div>
          <p style={{ fontSize: 13, fontWeight: 700, margin: '0 0 10px' }}>{t(locale, 'participationLedger')}</p>
          {participants.length === 0 ? (
            <p style={{ fontSize: 12, color: '#6b7280' }}>{isRTL ? 'لا يوجد مشاركون بعد' : 'No participants yet'}</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #111827' }}>
                  <th style={{ textAlign: isRTL ? 'right' : 'left', padding: '6px 4px' }}>
                    {isRTL ? 'المشارك' : 'Participant'}
                  </th>
                  <th style={{ textAlign: isRTL ? 'right' : 'left', padding: '6px 4px' }}>
                    {t(locale, 'joinedDate')}
                  </th>
                  <th style={{ textAlign: isRTL ? 'left' : 'right', padding: '6px 4px' }}>
                    {t(locale, 'treesReserved')}
                  </th>
                  <th style={{ textAlign: isRTL ? 'left' : 'right', padding: '6px 4px' }}>
                    {t(locale, 'treesPlanted')}
                  </th>
                  <th style={{ textAlign: isRTL ? 'left' : 'right', padding: '6px 4px' }}>
                    {t(locale, 'rewardsEarned')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p, i) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: i % 2 ? '#f9fafb' : '#ffffff' }}>
                    <td style={{ padding: '6px 4px', fontWeight: 600 }}>{p.participant_name}</td>
                    <td style={{ padding: '6px 4px', color: '#6b7280' }}>{p.joined_at}</td>
                    <td style={{ padding: '6px 4px', textAlign: isRTL ? 'left' : 'right' }}>
                      {p.trees_reserved.toLocaleString()}
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: isRTL ? 'left' : 'right' }}>
                      {p.trees_planted.toLocaleString()}
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: isRTL ? 'left' : 'right', fontWeight: 600 }}>
                      {p.rewards_earned.toLocaleString()} GP
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }
);
